import { type ButtonHTMLAttributes, forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";
import { Icon, type IconName } from "../icons/Icon";
import { ONLINE_ONLY, useOnline } from "../lib/online";

/* ===== ボタン ===== */
type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "text" | "danger";
  size?: "sm" | "md" | "lg";
  icon?: IconName;
  iconOnly?: boolean;
  block?: boolean;
  busy?: boolean;
  /** オンラインだけの操作。オフラインでは押せず「オンラインで使えます」と出す */
  online?: boolean;
  /** 押せない理由（ツールチップに出す） */
  disabledReason?: string;
};

export const Button = forwardRef<HTMLButtonElement, BtnProps>(function Button(
  { variant = "secondary", size = "md", icon, iconOnly, block, busy, online: needsOnline, disabledReason, className, children, disabled, title, onClick, ...rest },
  ref,
) {
  const isOnline = useOnline();
  const offlineBlocked = needsOnline && !isOnline;
  const blocked = disabled || busy || offlineBlocked;
  const reason = offlineBlocked ? ONLINE_ONLY : disabled ? disabledReason : undefined;
  const [showReason, setShowReason] = useState(false);
  const cls = [
    "btn",
    variant === "primary" && "btn-primary",
    variant === "text" && "btn-text",
    variant === "danger" && "btn-danger",
    size === "sm" && "btn-sm",
    size === "lg" && "btn-lg",
    iconOnly && "btn-icon",
    block && "btn-block",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const btn = (
    <button
      ref={ref}
      type="button"
      className={cls}
      aria-disabled={blocked || undefined}
      aria-busy={busy || undefined}
      title={iconOnly ? (title ?? (typeof children === "string" ? children : undefined)) : title}
      onClick={(e) => {
        if (blocked) {
          e.preventDefault();
          if (reason) {
            setShowReason(true);
            window.setTimeout(() => setShowReason(false), 2400);
          }
          return;
        }
        onClick?.(e);
      }}
      {...rest}
    >
      {busy ? <span className="spinner" aria-hidden /> : icon ? <Icon name={icon} /> : null}
      {iconOnly ? <span className="sr-only">{children}</span> : children}
    </button>
  );
  if (!reason) return btn;
  // 押せない理由は、重ねたとき・フォーカスしたとき（CSS）と、押したとき（2.4 秒）に出す
  return (
    <span className={showReason ? "tip-anchor show" : "tip-anchor"}>
      {btn}
      <span className="tip" role="status">
        {reason}
      </span>
    </span>
  );
});

export function LinkButton({
  to,
  variant = "secondary",
  size = "md",
  icon,
  children,
  testId,
}: {
  to: string;
  variant?: "primary" | "secondary" | "text";
  size?: "sm" | "md" | "lg";
  icon?: IconName;
  children: ReactNode;
  testId?: string;
}) {
  const cls = ["btn", variant === "primary" && "btn-primary", variant === "text" && "btn-text", size === "sm" && "btn-sm", size === "lg" && "btn-lg"].filter(Boolean).join(" ");
  return (
    <Link to={to} className={cls} data-testid={testId}>
      {icon && <Icon name={icon} />}
      {children}
    </Link>
  );
}

/* ===== 入力欄 ===== */
type FieldProps = { label: ReactNode; error?: string | null; hint?: ReactNode; children: (id: string, describedBy: string | undefined) => ReactNode; className?: string };
export function Field({ label, error, hint, children, className }: FieldProps) {
  const id = useId();
  const errId = `${id}-err`;
  return (
    <div className={className ? `field ${className}` : "field"}>
      <label htmlFor={id}>{label}</label>
      {children(id, error ? errId : undefined)}
      {hint && !error && <span className="field-hint">{hint}</span>}
      {error && (
        <span className="err" id={errId}>
          {error}
        </span>
      )}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; unit?: string; error?: string | null; hint?: ReactNode; fieldClass?: string };
export const TextField = forwardRef<HTMLInputElement, InputProps>(function TextField({ label, unit, error, hint, fieldClass, className, ...rest }, ref) {
  return (
    <Field label={label} error={error} hint={hint} className={fieldClass}>
      {(id, describedBy) => (
        <div className="input-wrap">
          <input ref={ref} id={id} className={className ? `input ${className}` : "input"} aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...rest} />
          {unit && <span className="unit">{unit}</span>}
        </div>
      )}
    </Field>
  );
});

/** 数値の入力（空は null）。整数だけ */
export function NumberField({
  label,
  value,
  onChange,
  unit,
  min,
  max,
  error,
  hint,
  disabled,
  fieldClass,
  step,
  decimals = 0,
  placeholder,
  testId,
  grouping = false,
}: {
  label: ReactNode;
  value: number | null;
  onChange: (v: number | null) => void;
  unit?: string;
  min?: number;
  max?: number;
  error?: string | null;
  hint?: ReactNode;
  disabled?: boolean;
  fieldClass?: string;
  step?: number;
  decimals?: number;
  placeholder?: string;
  testId?: string;
  /** 入力していないときは桁区切り（10,000）で見せる。入力中は数字だけ。値は変わらない */
  grouping?: boolean;
}) {
  const [text, setText] = useState(value === null ? "" : String(value));
  const [focused, setFocused] = useState(false);
  const last = useRef(value);
  useEffect(() => {
    if (value !== last.current) {
      last.current = value;
      setText(value === null ? "" : String(value));
    }
  }, [value]);
  return (
    <TextField
      label={label}
      unit={unit}
      error={error}
      hint={hint}
      disabled={disabled}
      fieldClass={fieldClass}
      className="num"
      inputMode={decimals > 0 ? "decimal" : "numeric"}
      placeholder={placeholder}
      data-testid={testId}
      value={grouping && !focused ? groupedText(text, value, decimals) : text}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      min={min}
      max={max}
      step={step}
      onChange={(e) => {
        setText(e.target.value);
        const v = parseNumberInput(e.target.value, decimals);
        last.current = v;
        onChange(v);
      }}
    />
  );
}

/** 桁区切りで見せる文字。入れた文字が値として読めないとき（誤り）は、入れたまま見せる */
export function groupedText(text: string, value: number | null, decimals = 0): string {
  // 丸める前の値で比べる（整数の欄に 1.5 と入れたとき、2 と見せないように）
  if (value === null || parseNumberInput(text, 10) !== value) return text;
  return value.toLocaleString("ja-JP", { maximumFractionDigits: decimals });
}

/** 「1,200」「１２００」も受ける。読めない値は null */
export function parseNumberInput(raw: string, decimals = 0): number | null {
  const s = raw
    .replace(/[０-９．－]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[,，\s]/g, "")
    .trim();
  if (!s) return null;
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  const f = 10 ** decimals;
  return Math.round(n * f) / f;
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & { label: ReactNode; error?: string | null; hint?: ReactNode; fieldClass?: string };
export function SelectField({ label, error, hint, fieldClass, children, ...rest }: SelectProps) {
  return (
    <Field label={label} error={error} hint={hint} className={fieldClass}>
      {(id, describedBy) => (
        <select id={id} className="select" aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...rest}>
          {children}
        </select>
      )}
    </Field>
  );
}

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { label: ReactNode; error?: string | null; hint?: ReactNode; fieldClass?: string };
export function TextareaField({ label, error, hint, fieldClass, ...rest }: TextareaProps) {
  return (
    <Field label={label} error={error} hint={hint} className={fieldClass}>
      {(id, describedBy) => <textarea id={id} className="textarea" aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...rest} />}
    </Field>
  );
}

/* ===== 札 ===== */
export type Tone = "ok" | "check" | "ng" | "ai" | "neutral" | "primary";
const TONE_MARK: Partial<Record<Tone, string>> = { ok: "✓", check: "!", ng: "×" };
/** 札（状態）。色だけで伝えないよう、よい・確認が必要・だめには記号を必ず付ける */
export function Tag({ tone, children, title, testId }: { tone: Tone; children: ReactNode; title?: string; testId?: string }) {
  const mark = TONE_MARK[tone];
  return (
    <span className={`tag tag-${tone}`} title={title} data-testid={testId}>
      {mark && <span aria-hidden>{mark}</span>}
      {children}
    </span>
  );
}

/* ===== 見出し ===== */
export function PageHead({ title, en, crumbs, actions }: { title: ReactNode; en?: string; crumbs?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div className="title">
        {crumbs && <div className="crumbs">{crumbs}</div>}
        {en && <span className="label-en">{en}</span>}
        <h1>{title}</h1>
        <span className="accent" aria-hidden />
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  );
}

/* ===== 読み込み・空・失敗 ===== */
export function Loading({ full, label = "読み込んでいます" }: { full?: boolean; label?: string }) {
  return (
    <div className={full ? "loading loading-full" : "loading"} role="status" aria-live="polite">
      <span className="spinner" aria-hidden />
      <span>{label}</span>
    </div>
  );
}

export function Empty({ icon, text, action, compact, testId }: { icon: IconName; text: ReactNode; action?: ReactNode; compact?: boolean; testId?: string }) {
  return (
    <div className={compact ? "empty compact" : "empty"} data-testid={testId}>
      <Icon name={icon} size={28} />
      <p>{text}</p>
      {action}
    </div>
  );
}

export function Failed({ message, onRetry, compact }: { message: string; onRetry?: () => void; compact?: boolean }) {
  return (
    <div className={compact ? "empty compact error" : "empty error"} role="alert">
      <Icon name="warning" size={28} />
      <p>{message}</p>
      {onRetry && (
        <Button icon="sync" onClick={onRetry}>
          もう一度読み込む
        </Button>
      )}
    </div>
  );
}

export function Notice({ tone = "info", icon, children, testId }: { tone?: "info" | "check" | "ng" | "ok"; icon?: IconName; children: ReactNode; testId?: string }) {
  const ic: IconName = icon ?? (tone === "ng" || tone === "check" ? "warning" : tone === "ok" ? "check" : "info");
  return (
    <div className={`notice notice-${tone}`} role={tone === "ng" ? "alert" : undefined} data-testid={testId}>
      <Icon name={ic} />
      <div className="grow">{children}</div>
    </div>
  );
}

/** オフラインのとき、オンラインだけの画面の代わりに出す */
export function OnlineOnlyNotice() {
  return <Empty icon="offline" text="この画面はオンラインで使えます。電波のある所で開いてください。" />;
}

/* ===== ツールチップ ===== */
export function Tip({ text, children }: { text: ReactNode; children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <span className={open ? "tip-anchor show" : "tip-anchor"}>
      {children ?? (
        <button type="button" className="btn btn-text btn-sm btn-icon" aria-label="説明" onClick={() => setOpen((o) => !o)}>
          <Icon name="help" />
        </button>
      )}
      <span className="tip" role="tooltip">
        {text}
      </span>
    </span>
  );
}

/* ===== モーダル ===== */
export function Modal({ open, title, onClose, children, footer, wide }: { open: boolean; title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
    }
    if (!open && d.open) {
      if (typeof d.close === "function") d.close();
      else d.removeAttribute("open");
    }
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={wide ? "modal wide" : "modal"}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      aria-labelledby={open ? "modal-title" : undefined}
    >
      {open && (
        <>
          <div className="modal-head">
            <h2 id="modal-title">{title}</h2>
            <Button variant="text" size="sm" icon="close" iconOnly onClick={onClose}>
              閉じる
            </Button>
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-foot">{footer}</div>}
        </>
      )}
    </dialog>
  );
}

/** 確認の小さいモーダル（confirm の代わり） */
export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel,
  danger,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>キャンセル</Button>
          <Button variant={danger ? "danger" : "primary"} busy={busy} onClick={onConfirm} data-testid="confirm-ok">
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  );
}

/* ===== ポップオーバー（外を押すと閉じる） ===== */
export function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
}
